import { SelectInput } from 'canoe';
import debounce from 'lodash/debounce';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { ContentMetadata } from '@candu-ai/content/types';
import { PlaceholderParagraph } from 'commons/containers/MediaLibrary';
import { CONTENT_PUBLISHED_STATES } from 'commons/models';
import DashedOutline from 'craft/components/DashedOutline';
import { Content as ContentIcon } from 'craft/icons';
import ContentPublishedState from 'dashboard/pages/Content/components/ContentPublishedState';
import { OptionsWrapper } from 'styleguide/components/Input/Select';

const Container = styled(DashedOutline)`
  min-width: 120px;
  min-height: 320px;
  width: 100%;
  padding-top: 40px;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  ${OptionsWrapper} {
    max-height: 140px;
  }
`;

interface StateProps {
  semaphoreOnly: boolean;
  content: any;
}

const State = styled(ContentPublishedState)<StateProps>`
  height: 8px;
  margin-left: 6px;
  margin-top: 5px;
  width: 8px;
`;

interface EmptyStateProps {
  setSlug: (slug: string) => void;
  contentList: ContentMetadata[];
}

export const EmptyState = ({ setSlug, contentList }: EmptyStateProps) => {
  const [searchValue, setSearchValue] = useState('');

  const options = contentList.map((content) => ({
    value: content.slug,
    label: content.name,
    prefix: (
      <State
        semaphoreOnly
        content={{
          lastModifiedAt: content.lastModifiedAt,
          lastPublishedAt: content.lastPublishedAt,
          publishedState: content.lastPublishedAt
            ? CONTENT_PUBLISHED_STATES.PUBLISHED
            : CONTENT_PUBLISHED_STATES.DRAFT,
        }}
      />
    ),
  }));

  const filteredOptions = useMemo(() => {
    if (!searchValue) {
      return options;
    }
    return options.filter(({ label }: { label: string }) => {
      return label.toLowerCase().includes(searchValue?.toLowerCase());
    });
  }, [searchValue, options]);

  const updateSearchValue = debounce((e) => {
    setSearchValue(e.target.value);
  }, 300);

  const clearSearchValue = () => setSearchValue('');

  const onSelectValue = (select: { value: string } | null) => {
    if (select) {
      setSlug(select.value);
    }
  };

  return (
    <Container>
      <ContentIcon size={34} />
      <PlaceholderParagraph size="short" align="center" isTitle noMargin>
        Embed Content
      </PlaceholderParagraph>
      <PlaceholderParagraph size="short" align="center">
        Select content to embed from the dropdown menu:
      </PlaceholderParagraph>
      <div style={{ width: '300px' }}>
        <SelectInput
          scale="small"
          searchable
          placeholder="Select Content"
          searchInputProps={{
            placeholder: 'Search Content',
            ignoreEvents: true,
          }}
          items={filteredOptions}
          onOpeningChange={(isOpen: boolean) => {
            if (!isOpen) {
              clearSearchValue();
            }
          }}
          onFilter={updateSearchValue}
          onClearFilter={clearSearchValue}
          onSelect={onSelectValue}
        />
      </div>
    </Container>
  );
};
